/* @flow */
import { isEmpty, capitalize, omit } from 'lodash';
import * as React from 'react';

import AvatarWithTextItem from 'components/AvatarWithTextItem';
import AvatarWithTextItems from 'components/AvatarWithTextItems';
import EventTracking from 'lib/EventTracking';
import Text from 'components/Text';
import Button from 'components/ButtonDeprecated';
import LoadingIndicator from 'components/LoadingIndicator';
import Profile from 'components/Profile';
import Group from 'entities/Group';
import Message from 'entities/Message';
import User from 'entities/User';
import AnnouncementMessageListItem from 'chat/components/AnnouncementMessageListItem';
import GroupProfileHeader from 'groups/components/GroupProfileHeader';
import UserActionsMenuButton from 'user/components/UserActionsMenuButton';
import GroupActionsMenuButton from 'groups/components/GroupActionsMenuButton';
import Buttons from 'components/Buttons';
import Feature from 'components/Feature';
import { shapeMemberIntoUserProfile } from 'users/components/UsersProfile/UsersProfileInGroupContainer';
import mapGraphQLMemberToUserActionsMenuButtonUser from 'user/lib/mapGraphQLMemberToUserActionsMenuButtonUser';

import './GroupProfile.scss';

import type {
  ClassProfile_classes,
  ClassProfile_classes_members,
  ClassProfile_classes_owners,
} from 'groups/queries/__generated__/ClassProfile';
import type { UserProfile } from 'users/components/UsersProfile';

export type GroupProfileType = $Shape<ClassProfile_classes>;

export type Props = {
  group: GroupProfileType,
  loading: boolean,
  onBackClicked?: () => void,
  onClose: () => void,
  onRemoveGroupFromOrg: ?() => void,
  onSubscribe: () => void,
  onUnsubscribe: () => void,
  onUserChanged: () => void,
  onUserClicked: (user: UserProfile, role: string) => void,
  onViewAllAnnouncementsClicked: (groupUuid: string) => void,
  openModalRemoveGroupFromOrgEventKey: string,
};

export default class GroupProfile extends React.Component<Props> {
  getSections = () => {
    const { group, loading, onViewAllAnnouncementsClicked } = this.props;

    if (loading) {
      return [];
    }

    const { members, membershipsCount, owners, ownersCount, broadcastCompositeStream } = group;

    const groupEntity = ((Group.fromJSON(group): any): Group);

    const sections = [];

    if (owners) {
      sections.push({
        title: 'Class owners',
        children: this.renderGroupMembersList(owners, 'owned', ownersCount),
        count: owners.length,
      });
    }

    if (members) {
      sections.push({
        title: 'Participants',
        children: this.renderGroupMembersList(members, 'joined', membershipsCount),
        count: membershipsCount,
      });
    }

    if (broadcastCompositeStream && broadcastCompositeStream.length > 0) {
      const messages = broadcastCompositeStream.map(m => Message.fromJSON(m));
      const latestMessage = messages[0];

      const lastAnnouncementSection = {
        title: 'Last announcement',
        children: (
          <React.Fragment>
            {messages && (
              <ul>
                <AnnouncementMessageListItem
                  messages={[latestMessage]}
                  group={groupEntity}
                  isGroupChat={false}
                  disableReactionToggle={false}
                  showProfilePic={false}
                />
              </ul>
            )}
            <Button
              color="mascot"
              level="text"
              weight="semibold"
              onClick={() => onViewAllAnnouncementsClicked(group.uuid)}>
              View more announcements
            </Button>
          </React.Fragment>
        ),
        collapsible: false,
      };

      sections.unshift(lastAnnouncementSection);
    }

    return sections;
  };

  handleRemoveGroupFromOrg = () => {
    const { onClose, onRemoveGroupFromOrg, onUserChanged } = this.props;
    onClose();
    onUserChanged();

    if (onRemoveGroupFromOrg) {
      onRemoveGroupFromOrg();
    }
  };

  handleSectionVisibilityChange = (uuidsOfExpandedSections: string[]) => {
    if (uuidsOfExpandedSections.includes('Class owners')) {
      EventTracking.trackKey('admin_dashboard.class_list.profile_view.class_owners.expand.click');
    }
    if (uuidsOfExpandedSections.includes('Participants')) {
      EventTracking.trackKey('admin_dashboard.class_list.profile_view.participants.expand.click');
    }
  };

  roleDescriptionForMember(member: ClassProfile_classes_members) {
    const { user, org_affiliation_view } = member;

    if (org_affiliation_view) {
      if (org_affiliation_view.roles.length > 0) {
        return capitalize(org_affiliation_view.roles.join(', '));
      } else {
        return 'No role';
      }
    } else {
      return capitalize(user.role);
    }
  }

  renderAndMoreText = (
    members:
      | $ReadOnlyArray<ClassProfile_classes_owners>
      | $ReadOnlyArray<ClassProfile_classes_members>,
    groupType: 'owned' | 'joined',
    count: number
  ) => {
    if (count > members.length) {
      const type = groupType === 'owned' ? 'class owners' : 'participants';

      return (
        <Text className="GroupProfile-andMoreText" color="graphite">
          {`Showing ${members.length} of the ${count} ${type}`}
        </Text>
      );
    }

    return null;
  };

  renderGroupMembersList = (
    members:
      | $ReadOnlyArray<ClassProfile_classes_owners>
      | $ReadOnlyArray<ClassProfile_classes_members>,
    groupType: 'owned' | 'joined',
    count: number
  ) => {
    if (isEmpty(members)) {
      return null;
    }

    const { onUserClicked, onUserChanged, group } = this.props;
    const groupEntity = ((Group.fromJSON(group): any): Group);

    return (
      <AvatarWithTextItems>
        {members.map(member => {
          const { user } = member;
          const roleDescription = this.roleDescriptionForMember(member);

          const shapedMember = shapeMemberIntoUserProfile(member);
          if (!shapedMember) {
            return null;
          }

          return (
            <AvatarWithTextItem
              subtle
              user={User.fromJSON(user)}
              subtext={roleDescription}
              key={user.id}
              onClick={() => onUserClicked(omit(user, 'role'), roleDescription)}
              action={
                groupType === 'joined' ? (
                  <UserActionsMenuButton
                    buttonSize="xlarge"
                    level="icon"
                    glyphSize="inherit"
                    groupToAdmin={groupEntity}
                    isAdminView
                    showRemoveMember
                    onUserRemoved={onUserChanged}
                    user={mapGraphQLMemberToUserActionsMenuButtonUser(shapedMember)}
                    tooltip="More actions"
                    tooltipPlacement="top"
                  />
                ) : null
              }
            />
          );
        })}
        {this.renderAndMoreText(members, groupType, count)}
      </AvatarWithTextItems>
    );
  };

  renderJoinLeaveButton() {
    const { group, onSubscribe, onUnsubscribe } = this.props;

    const { permissions, isMember } = group;

    const canSubscribe = permissions ? permissions.can_join : false;
    const canUnsubscribe = permissions ? permissions.can_leave : false;
    const joinLeaveButtonProps = {
      full: true,
    };

    if (isMember) {
      if (canUnsubscribe) {
        return (
          <Button
            {...joinLeaveButtonProps}
            color="apple"
            level="secondary"
            data-test="leave-btn"
            onClick={onUnsubscribe}>
            Leave this class
          </Button>
        );
      }
    } else {
      if (canSubscribe) {
        return (
          <Button
            {...joinLeaveButtonProps}
            level="primary"
            data-test="join-btn"
            onClick={onSubscribe}>
            Join this class
          </Button>
        );
      }
    }

    return null;
  }

  renderActions = () => {
    const { loading, group, openModalRemoveGroupFromOrgEventKey } = this.props;

    if (loading) {
      return null;
    }

    const { name } = group;

    const joinLeaveButton = this.renderJoinLeaveButton();

    const goToButton = (
      <Button level="primary" to={`/classes/${name}`}>
        Go to this class
      </Button>
    );

    return (
      <Buttons fullButtonIndex={0}>
        {joinLeaveButton || goToButton}
        <Feature
          name="org_unlink.web.mar-19"
          on={
            <GroupActionsMenuButton
              group={group}
              onRemoveGroupFromOrg={this.handleRemoveGroupFromOrg}
              openModalRemoveGroupFromOrgEventKey={openModalRemoveGroupFromOrgEventKey}
            />
          }
        />
      </Buttons>
    );
  };

  render() {
    const { group, onClose, onBackClicked, loading } = this.props;

    return (
      <Profile
        actions={this.renderActions()}
        header={<GroupProfileHeader group={group} />}
        loadingIndicator={loading && <LoadingIndicator align="center" size="small" />}
        sections={this.getSections()}
        className="GroupProfile"
        onBackClicked={onBackClicked}
        onClose={onClose}
        onChange={this.handleSectionVisibilityChange}
      />
    );
  }
}
